import { Mail, FileText, ShieldCheck, Plane, Stamp, PackageCheck, Truck, Send } from "lucide-react";

const DEF = {
  pre_alert:           { label: "Pre-Alert Overview",  icon: Mail,         actors: ["broker_csr"] },
  documentation:       { label: "Documentation",       icon: FileText,     actors: ["broker_csr"] },
  customs_declaration: { label: "Customs Declaration", icon: ShieldCheck,  actors: ["declarant"], slot: "declarant" },
  logistics:           { label: "Booking / Logistics", icon: Plane,        actors: ["forwarder"] },
  processing:          { label: "Processing",          icon: Stamp,        actors: ["processor"], slot: "processor" },
  release:             { label: "Release",             icon: PackageCheck, actors: ["processor"], slot: "processor" },
  delivery:            { label: "Delivery",            icon: Truck,        actors: ["delivery"], slot: "delivery" },
  transmittal:         { label: "Billing Transmittal", icon: Send,         actors: "all" },
};

export const PIPELINES = {
  brokerage: ["pre_alert", "documentation", "customs_declaration", "processing", "release", "delivery", "transmittal"],
  forwarding: ["pre_alert", "documentation", "transmittal"],
};
export const FWD_DOC_ROLES = ["admin", "broker_manager", "broker_csr", "forwarder", "declarant"];
export const FWD_STATUSES = [["Validated", "Validated"], ["No Booking", "No Booking"], ["Other", "Other (Specify)"]];
export const SHIPMENT_TYPES = [["brokerage", "Brokerage"], ["forwarding", "Forwarding"]];

export const STAGES = Object.entries(DEF).map(([key, v]) => ({ key, ...v }));
export const stagesFor = (type) => (PIPELINES[type] || PIPELINES.brokerage).map((key) => ({ key, ...DEF[key] }));
export const stageLabel = (key) => DEF[key]?.label || key;

export const SUPERVISORS = ["admin", "broker_manager"];
export const BROKER_ROLES = ["admin", "broker_manager", "broker_csr"];
export const FIN_ROLES = ["admin", "finance_staff", "finance_manager"];
export const COMPLETED_ROLES = ["admin", "broker_manager", "broker_csr", "finance_staff", "finance_manager"];
export const BILLER_ROLES = ["finance_staff", "finance_manager"];
export const APPROVER_ROLES = ["admin", "finance_manager"];
export const SHARE_ROLES = ["admin", "broker_manager", "broker_csr"];

export const canActStage = (role, stageKey) => {
  if (!role) return false;
  if (SUPERVISORS.includes(role)) return true;
  const a = DEF[stageKey]?.actors;
  return a === "all" || (a || []).includes(role);
};

// Who may work on a stage of this specific shipment (mirrors backend check_stage_access).
export const stageAccess = (user, sh, key) => {
  const type = sh.type || "brokerage";
  if (!user || !PIPELINES[type].includes(key)) return { ok: false, reason: "Not part of this shipment." };
  if (SUPERVISORS.includes(user.role) || key === "transmittal") return { ok: true };
  if (type === "forwarding" && key === "documentation") {
    return FWD_DOC_ROLES.includes(user.role) ? { ok: true } : { ok: false, reason: "Read-only. Editable by Admin, Brokers, Forwarders and Declarants." };
  }
  const slot = type === "brokerage" ? DEF[key].slot : null;
  if (slot) {
    const a = sh.assignments?.[slot];
    if (a?.id === user.id) return { ok: true };
    return { ok: false, reason: a ? `Only the assigned ${slot} (${a.name}) can work on this step.` : `No ${slot} assigned yet. Assign one in Documentation.` };
  }
  return DEF[key].actors.includes(user.role)
    ? { ok: true }
    : { ok: false, reason: `Read-only. This step is handled by ${DEF[key].actors.map((r) => ROLE_LABELS[r]).join(", ")}.` };
};

export const ROLE_LABELS = {
  admin:          "Admin",
  broker_csr:     "Broker — CSR",
  broker_manager: "Broker — Ops/Asst. Manager",
  declarant:      "Declarant",
  forwarder:      "Forwarder",
  finance_staff:   "Finance Staff",
  finance_manager: "Finance Manager",
  sales:          "Sales",
  processor:      "Processor",
  delivery:       "Delivery",
};

export const ROLE_BADGE = {
  admin:          "bg-rose-500/15 text-rose-300 border border-rose-500/30",
  broker_csr:     "bg-blue-500/15 text-blue-300 border border-blue-500/30",
  broker_manager: "bg-indigo-500/15 text-indigo-300 border border-indigo-500/30",
  declarant:      "bg-amber-500/15 text-amber-300 border border-amber-500/30",
  forwarder:      "bg-cyan-500/15 text-cyan-300 border border-cyan-500/30",
  finance_staff:   "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
  finance_manager: "bg-lime-500/15 text-lime-300 border border-lime-500/30",
  sales:          "bg-violet-500/15 text-violet-300 border border-violet-500/30",
  processor:      "bg-teal-500/15 text-teal-300 border border-teal-500/30",
  delivery:       "bg-orange-500/15 text-orange-300 border border-orange-500/30",
};

export const CUSTOMS_BADGE = {
  "Pending":  "bg-slate-500/15 text-slate-300 border border-slate-500/30",
  "Lodged":   "bg-blue-500/15 text-blue-300 border border-blue-500/30",
  "Released": "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
  "On Hold":  "bg-rose-500/15 text-rose-300 border border-rose-500/30",
};

export const DELIVERY_BADGE = {
  "Scheduled":  "bg-slate-500/15 text-slate-300 border border-slate-500/30",
  "In Transit": "bg-amber-500/15 text-amber-300 border border-amber-500/30",
  "Delivered":  "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
  "Failed":     "bg-rose-500/15 text-rose-300 border border-rose-500/30",
};

export const TRANSMITTAL_BADGE = {
  "Draft":        "bg-slate-500/15 text-slate-300 border border-slate-500/30",
  "Transmitted":  "bg-amber-500/15 text-amber-300 border border-amber-500/30",
  "Acknowledged": "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
};

export const PAYMENT_BADGE = {
  "Unpaid":  "bg-rose-500/15 text-rose-300 border border-rose-500/30",
  "Outstanding": "bg-rose-500/15 text-rose-300 border border-rose-500/30",
  "Partial": "bg-amber-500/15 text-amber-300 border border-amber-500/30",
  "Paid":    "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
};
